import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "../Logo";
import { smoothScrollTo } from "../SmoothScroll";
import { openWhatsApp } from "../../lib/whatsapp";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Why Us", href: "#why-us" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (href) => (e) => {
    e.preventDefault();
    const wasOpen = mobileOpen;
    setMobileOpen(false);
    // If the mobile menu was open, wait for its close animation to finish
    // before scrolling (framer-motion measures height:'auto' and briefly
    // resets window scrollY, which cancels a fresh native smooth-scroll).
    if (wasOpen) {
      setTimeout(() => smoothScrollTo(href), 420);
    } else {
      smoothScrollTo(href);
    }
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#FAF7F0]/85 backdrop-blur-xl border-b border-charcoal/10 shadow-[0_10px_30px_rgba(31,94,58,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-[88px] md:h-[104px] flex items-center justify-between">
        <button
          onClick={handleAnchor("#home")}
          data-testid="nav-logo-button"
          className="focus:outline-none"
          aria-label="Go to top"
        >
          <Logo compact variant={scrolled ? "dark" : "light"} />
        </button>

        <nav
          className="hidden lg:flex items-center gap-10"
          aria-label="Primary"
        >
          {NAV_LINKS.map((l, i) => (
            <motion.a
              key={l.href}
              href={l.href}
              onClick={handleAnchor(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`link-underline font-sans text-[13px] uppercase tracking-[0.22em] transition-colors ${
                scrolled
                  ? "text-charcoal/80 hover:text-forest"
                  : "text-cream/85 hover:text-gold"
              }`}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.05, duration: 0.6 }}
            >
              {l.label}
            </motion.a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              openWhatsApp(
                "Hello, I'd like to enquire about premium green cardamom from The Cardamom Trading Co."
              )
            }
            data-testid="nav-enquire-cta"
            className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] font-medium transition-colors ${
              scrolled
                ? "bg-forest text-cream hover:bg-forestDeep"
                : "bg-gold text-forestDeep hover:bg-cream"
            }`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                scrolled ? "bg-gold" : "bg-forest"
              }`}
              aria-hidden
            />
            Enquire on WhatsApp
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
              scrolled
                ? "border-charcoal/15 text-charcoal hover:bg-charcoal/5"
                : "border-cream/40 text-cream hover:bg-cream/10"
            }`}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-charcoal/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={handleAnchor(l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif text-3xl text-charcoal hover:text-forest transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  openWhatsApp();
                }}
                data-testid="mobile-enquire-cta"
                className="mt-4 self-start inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-[12px] uppercase tracking-[0.22em]"
              >
                Enquire on WhatsApp
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
